// enhancerMeta.ts — the single source of truth for the Video Enhancer's data
// contracts with the AI pipeline (NorthShore hey-joe-LLM Lambda via the
// /api/shotstack/* and /api/script/* proxies).
//
// It provides:
//  1. EnhancerFile — one honest file type (superset of every field the old
//     code read via `(file as any)`).
//  2. findFileAnalysis — the ONE analysis-results→file matching selector
//     (previously duplicated in 5+ places).
//  3. buildAssetsMetadata — the ONE assets_metadata builder, parameterized by
//     variant. Field shapes are ported exactly from the legacy component:
//       - 'render'        → { url, name, duration:"M:SS", trim_start?, trim_end?, comment? }
//                           (images: width/height as STRINGS; audio: absolute S3 url)
//       - 'script-analyze'→ { name, type, duration:number, comment, liked, talent, takeName, s3Url }
//       - 'plan'          → { url, name, type, duration:String(number), comment, liked }
//       - 'refine'        → { name, type, duration:number, comment }
//       - 'generate-plan' → { name, type, duration:number, s3Url, comment, thumbnailUrl }
//     Segment timing tolerance: every variant accepts BOTH `start|start_time`
//     and `end|end_time` (fixes the legacy plan-mode inconsistency where
//     Whisper-shaped segments silently produced no transcript).
//
// DO NOT change payload shapes here without checking the Lambda contract —
// the NorthShore pipeline expects these exact fields.

import type { ProjectFile as BaseProjectFile } from '@/types/video-enhancer';
import type { ProjectClip } from '@/components/video-editor/TimelineSequence';
import { resolveVideoUrl } from '@/lib/utils';

/** The honest file type: everything the enhancer actually reads. */
export interface EnhancerFile extends BaseProjectFile {
  url?: string;              // occasionally set on re-imported/staged files
  castingComments?: string;  // casting.heyjoe.io feedback carried on imports
  referenceText?: string;    // user-corrected transcript text
  aspectRatio?: string;      // detected source ratio ('16:9' | '9:16' | '1:1' | '4:5' | 'source')
}

export type AnalysisResults = { [analysisId: string]: any };

// ---------------------------------------------------------------------------
// Analysis matching + transcript access
// ---------------------------------------------------------------------------

/** Match a file to its completed analysis entry (results keyed by analysis_id,
 * matched by video_id === file.id). Returns the `results` payload or null. */
export function findFileAnalysis(analysisResults: AnalysisResults | null | undefined, fileId: string | undefined): any | null {
  if (!fileId || !analysisResults) return null;
  const match = Object.values(analysisResults).find((result: any) =>
    result?.status === 'completed' &&
    (result.video_id === fileId || result.results?.video_id === fileId)
  );
  return match ? ((match as any).results || match) : null;
}

/** Tolerant segment field access — Whisper uses start/end, casting imports use start_time/end_time. */
export const segStart = (seg: any): number => Number(seg?.start ?? seg?.start_time ?? 0);
export const segEnd = (seg: any): number => Number(seg?.end ?? seg?.end_time ?? 0);
export const segText = (seg: any): string => seg?.text || seg?.content || '';

export interface TranscriptData {
  segments?: any[];
  words?: any[];
  text?: string;
  speakerCount?: number;
}

/** Cascade lookup: clip.analysisData → file.analysisData → matched analysisResults. */
export function getTranscript(
  file: EnhancerFile | undefined,
  clip: (ProjectClip & { analysisData?: any; referenceText?: string }) | null,
  analysisResults: AnalysisResults | null | undefined,
): TranscriptData {
  const fileAnalysis = findFileAnalysis(analysisResults, file?.id);
  const segments = clip?.analysisData?.transcript?.segments ||
                   (file?.analysisData as any)?.transcript?.segments ||
                   fileAnalysis?.transcript?.segments;
  const words = clip?.analysisData?.transcript?.words ||
                (file?.analysisData as any)?.transcript?.words ||
                fileAnalysis?.transcript?.words;
  const text = clip?.analysisData?.transcript?.text ||
               clip?.analysisData?.transcript?.full_text ||
               (file?.analysisData as any)?.transcript?.text ||
               (file?.analysisData as any)?.transcript?.full_text ||
               fileAnalysis?.transcript?.text ||
               fileAnalysis?.transcript?.full_text ||
               clip?.referenceText;
  const speakerCount = (file?.analysisData as any)?.transcript?.speaker_count ||
                       fileAnalysis?.transcript?.speaker_count;
  return { segments, words, text, speakerCount };
}

// ---------------------------------------------------------------------------
// Formatting helpers
// ---------------------------------------------------------------------------

/** Strip the "(1.2s-3.4s)" timing suffix from clip names. */
export const stripTimingSuffix = (name: string): string =>
  (name || '').replace(/\s*\([\d.]+s-[\d.]+s\)\s*/g, '').trim();

/** Format seconds as "M:SS" (the shape the Lambda's validation sets use). */
export function formatMSS(totalSeconds: number): string {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = Math.round(totalSeconds % 60);
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

/** Rewrite our /api/s3/ proxy paths to absolute S3 URLs (Shotstack + Lambda need absolute URLs). */
export function toAbsoluteS3Url(url: string | null | undefined): string {
  if (!url) return '';
  if (url.startsWith('/api/s3/')) {
    return `https://hey-joe-videos.s3.amazonaws.com/${url.replace('/api/s3/', '')}`;
  }
  return url;
}

/** Timed transcript block: `[0.0s-2.5s]: "Hello world"` per line.
 * With speakerLabels, prefixes ` Speaker N:` (spk_N → Speaker N+1). */
export function timedSegmentLines(segments: any[], opts?: { speakerLabels?: boolean }): string {
  return segments.map((seg: any) => {
    let speakerLabel = '';
    if (opts?.speakerLabels && seg.speaker) {
      if (typeof seg.speaker === 'string' && seg.speaker.startsWith('spk_')) {
        const num = parseInt(seg.speaker.replace('spk_', ''), 10) + 1;
        speakerLabel = ` Speaker ${num}:`;
      } else {
        speakerLabel = ` ${seg.speaker}:`;
      }
    }
    return `[${segStart(seg).toFixed(1)}s-${segEnd(seg).toFixed(1)}s]${speakerLabel} "${segText(seg)}"`;
  }).join('\n');
}

/** Word-level fallback: chunks of ~5 words with combined timing. */
export function timedWordChunks(words: any[]): string {
  const chunks: string[] = [];
  for (let i = 0; i < words.length; i += 5) {
    const chunk = words.slice(i, i + 5);
    const start = segStart(chunk[0]).toFixed(1);
    const end = segEnd(chunk[chunk.length - 1]).toFixed(1);
    const text = chunk.map((w: any) => w.word || w.text || '').join(' ');
    chunks.push(`[${start}s-${end}s]: "${text}"`);
  }
  return chunks.join('\n');
}

/** Standard transcript comment block (render/preview variant). */
export function buildTranscriptComment(t: TranscriptData): string {
  if (t.segments && t.segments.length > 0) {
    return `Transcript with timings:\n${timedSegmentLines(t.segments)}`;
  }
  if (t.words && t.words.length > 0) {
    return `Transcript with word timings:\n${timedWordChunks(t.words)}`;
  }
  if (t.text) return `Transcript: ${t.text}`;
  return '';
}

// ---------------------------------------------------------------------------
// assets_metadata builders
// ---------------------------------------------------------------------------

export type AssetsVariant = 'render' | 'script-analyze' | 'plan' | 'refine' | 'generate-plan';

export interface RenderAssetsResult {
  assetsMetadata: any[];
  lumaMatteMetadata: any[];
}

const isLumaClip = (c: ProjectClip): boolean =>
  c.layerType === 'mask' ||
  !!c.name?.includes('Luma Matte') ||
  !!c.name?.includes('foreground_mask') ||
  !!c.name?.includes('_mask');

/**
 * Variant 'render' (also used for /api/shotstack/preview): the canonical
 * per-clip payload for the Lambda `/generate` pipeline.
 */
export function buildRenderAssets(params: {
  clips: ProjectClip[];
  files: EnhancerFile[];
  analysisResults: AnalysisResults;
  includeTranscripts: boolean; // true for AI Creative (lambda) payloads
}): RenderAssetsResult {
  const { clips, files, analysisResults, includeTranscripts } = params;
  const assetsMetadata: any[] = [];
  const lumaMatteMetadata: any[] = [];

  const videoClips = clips.filter(c => c.fileType === 'video' && !isLumaClip(c));
  const lumaClips = clips.filter(c => c.fileType === 'video' && isLumaClip(c));
  const audioClips = clips.filter(c => c.fileType === 'audio');
  const imageClips = clips.filter(c => c.fileType === 'image');
  const otherClips = clips.filter(c => !['video', 'audio', 'image'].includes(c.fileType || '') && c.layerType !== 'mask');

  videoClips.forEach((clip, index) => {
    const file = files.find(f => f.id === clip.sourceFileId);
    if (!file || (!file.s3Url && !file.s3Key)) {
      throw new Error(`Missing S3 URL for clip: ${clip.name}`);
    }

    // Luma-matte pairs use the ORIGINAL video (the matte handles transparency).
    const hasLumaMatte = file.linkedMaskId || file.greenScreenType === 'foreground-mask';
    const resolvedS3Url = resolveVideoUrl(file.s3Url, file.s3Key);
    const videoUrl = hasLumaMatte ? resolvedS3Url : (file.greenScreenUrl || resolvedS3Url);

    const rawDuration = clip.outTime || clip.duration || file.duration || 5;
    const clipDuration = rawDuration - (clip.inTime || 0);
    const clipName = stripTimingSuffix(clip.name || `clip_${index + 1}`);

    let comment = '';
    if (includeTranscripts) {
      comment = buildTranscriptComment(getTranscript(file, clip as any, analysisResults));
    }

    // Casting feedback from imported takes
    const castingComments = (file as any).castingComments || (clip as any).castingComments;
    if (castingComments) {
      if (comment) comment += '\n\n';
      comment += `Casting Feedback: ${castingComments}`;
    }

    // Only send trim info if the user actually modified trim points —
    // otherwise let the AI pick intelligent trims.
    const fullDuration = file.duration || clip.duration || 5;
    const userModifiedTrim = (clip.inTime !== undefined && clip.inTime > 0) ||
                             (clip.outTime !== undefined && clip.outTime < fullDuration - 0.1);
    if (userModifiedTrim && clip.inTime !== undefined && clip.outTime !== undefined) {
      if (comment) comment += '\n\n';
      comment += `USER TRIM: ${clip.inTime.toFixed(1)}s to ${clip.outTime.toFixed(1)}s (USE THESE EXACT VALUES)`;
    }

    // Per-clip user instruction (chromaKey directives are structured elsewhere)
    if (clip.prompt && !clip.prompt.includes('chromaKey')) {
      const promptText = clip.prompt.trim().replace(/\s*volume[:\s]*[\d.]+/gi, '').trim();
      if (promptText) {
        if (comment) comment += '\n\n';
        comment += `CLIP INSTRUCTION: ${promptText}`;
      }
    }

    const metadata: any = {
      url: videoUrl,
      name: clipName,
      duration: formatMSS(clipDuration),
    };
    if (userModifiedTrim) {
      if (clip.inTime !== undefined && clip.inTime > 0) metadata.trim_start = clip.inTime;
      if (clip.outTime !== undefined) metadata.trim_end = clip.outTime;
    }
    if (comment) metadata.comment = comment;
    assetsMetadata.push(metadata);
  });

  audioClips.forEach((clip, index) => {
    const file = files.find(f => f.id === clip.sourceFileId);
    if (!file || !file.s3Url) {
      throw new Error(`Missing S3 URL for clip: ${clip.name}`);
    }
    const sourceInTime = clip.inTime || 0;
    const sourceOutTime = clip.outTime || clip.duration || file.duration || 5;
    const audioDuration = sourceOutTime - sourceInTime;

    const volumeMatch = clip.prompt?.match(/volume[:\s]*([\d.]+)/i);
    const volume = volumeMatch ? parseFloat(volumeMatch[1]) : 1.0;

    let audioComment = '';
    if (sourceInTime > 0) {
      audioComment = `start at :${Math.round(sourceInTime).toString().padStart(2, '0')}`;
    }
    if (volume !== 1.0) {
      if (audioComment) audioComment += ', ';
      audioComment += `volume ${volume}`;
    }

    const metadata: any = {
      // Shotstack needs absolute URLs — rewrite /api/s3/ proxy paths.
      url: toAbsoluteS3Url(file.s3Url),
      name: stripTimingSuffix(clip.name || `audio_${index + 1}`),
      duration: formatMSS(audioDuration),
    };
    if (audioComment) metadata.comment = audioComment;
    assetsMetadata.push(metadata);
  });

  imageClips.forEach((clip, index) => {
    const file = files.find(f => f.id === clip.sourceFileId);
    if (!file || !file.s3Url) {
      throw new Error(`Missing S3 URL for clip: ${clip.name}`);
    }
    const isBackground = clip.layerType === 'background' || clip.prompt?.toLowerCase().includes('background');
    const metadata: any = {
      url: file.s3Url,
      name: stripTimingSuffix(clip.name || (isBackground ? `background_${index + 1}` : `image_${index + 1}`)),
      // Width and height are REQUIRED by the Lambda for images — must be strings.
      width: String(file.width || 1920),
      height: String(file.height || 1080),
    };
    if (clip.prompt) metadata.comment = clip.prompt;
    assetsMetadata.push(metadata);
  });

  // Luma mattes go in a SEPARATE array — the Lambda LLM gets confused by
  // luma_matte assets; the backend injects them after the Lambda returns.
  lumaClips.forEach((clip, index) => {
    const file = files.find(f => f.id === clip.sourceFileId);
    if (file && file.s3Url) {
      const lumaDuration = clip.duration || file.duration || 5;
      lumaMatteMetadata.push({
        url: file.s3Url,
        s3Url: file.s3Url,
        name: `${clip.name}_luma_${index + 1}`,
        type: 'video',
        asset_type: 'luma_matte',
        track_type: 'luma_matte',
        duration: String(lumaDuration),
        layerType: 'mask',
        linkedPairId: file.linkedPairId || clip.linkedPairId,
        linkedVideoId: file.linkedVideoId,
      });
    }
  });

  otherClips.forEach((clip, index) => {
    const file = files.find(f => f.id === clip.sourceFileId);
    if (file && file.s3Url) {
      const otherDuration = clip.duration || file.duration || 5;
      assetsMetadata.push({
        url: file.s3Url,
        s3Url: file.s3Url,
        name: `${clip.name}_other_${index + 1}`,
        type: file.fileType || 'unknown',
        asset_type: file.fileType || 'unknown',
        track_type: 'overlay',
        duration: String(otherDuration), // duration must be STRING
      });
    }
  });

  return { assetsMetadata, lumaMatteMetadata };
}

/** Variant 'script-analyze' — the richest per-file payload for /api/script/analyze. */
export function buildScriptAnalyzeAssets(files: EnhancerFile[], analysisResults: AnalysisResults): any[] {
  return files.map(f => {
    const fileAnalysis = findFileAnalysis(analysisResults, f.id);
    const t = getTranscript(f, null, analysisResults);
    const backgroundType = (f.analysisData as any)?.background?.type ||
                           (fileAnalysis as any)?.background?.type;
    const visualLabels = (f.analysisData as any)?.visualLabels ||
                         (fileAnalysis as any)?.visualLabels;

    const commentParts: string[] = [];
    if (f.talent && f.talent.length > 0) {
      commentParts.push(`Talent: ${f.talent.join(', ')}`);
    }
    if (f.projectName || f.sessionName || f.groupName) {
      const context = [f.projectName, f.sessionName, f.groupName].filter(Boolean).join(' > ');
      commentParts.push(`Source: ${context}`);
    }
    if (f.takeName) {
      commentParts.push(`Take: ${f.takeName}`);
    }
    if (f.liked) {
      commentParts.push(`PREFERRED TAKE - This take was marked as liked/preferred by the casting team`);
    }
    if (f.comments && f.comments.length > 0) {
      const castingComments = f.comments.map((c: any) =>
        typeof c === 'string' ? c : c?.content || c?.text || c?.comment || ''
      ).filter(Boolean);
      if (castingComments.length > 0) {
        commentParts.push(`Casting notes: ${castingComments.join('; ')}`);
      }
    }
    if (backgroundType) {
      const bgLabel = backgroundType === 'green' ? 'Green screen background' :
                      backgroundType === 'blue' ? 'Blue screen background' :
                      'Natural/location background';
      commentParts.push(`Background: ${bgLabel}`);
    }
    if (visualLabels?.scenes && visualLabels.scenes.length > 0) {
      const sceneDescriptions = visualLabels.scenes.map((scene: any) => {
        const start = scene.startTime?.toFixed(1) || '0';
        const end = scene.endTime?.toFixed(1) || '0';
        return `[${start}s-${end}s]: ${scene.description}`;
      }).join('\n');
      commentParts.push(`Visual scenes:\n${sceneDescriptions}`);
    } else if (visualLabels?.labels && visualLabels.labels.length > 0) {
      commentParts.push(`Visual content: ${visualLabels.labels.join(', ')}`);
    }

    if (t.segments && t.segments.length > 0) {
      commentParts.push(`Transcript with timings:\n${timedSegmentLines(t.segments)}`);
    } else if (t.text) {
      commentParts.push(`Transcript: ${t.text}`);
    }

    return {
      name: f.name,
      type: f.fileType || 'video',
      duration: f.duration,
      comment: commentParts.join('\n'),
      liked: f.liked || false,
      talent: f.talent || [],
      takeName: f.takeName || null,
      s3Url: f.s3Url,
    };
  });
}

/** Variant 'plan' — /api/script/plan payload (selected files only, speaker-labeled transcript). */
export function buildPlanAssets(files: EnhancerFile[], selectedFiles: Set<string>, analysisResults: AnalysisResults): any[] {
  // Only selected files go to the plan endpoint (callers guard against an empty set).
  const filesToUse = files.filter(f => selectedFiles.has(f.id));
  return filesToUse.map(f => {
    const t = getTranscript(f, null, analysisResults);

    const commentParts: string[] = [];
    if (f.talent && f.talent.length > 0) {
      commentParts.push(`Talent: ${f.talent.join(', ')}`);
    }
    if (f.liked) {
      commentParts.push(`PREFERRED TAKE`);
    }
    if (t.speakerCount && t.speakerCount > 1) {
      commentParts.push(`Speakers detected: ${t.speakerCount} (if talent is listed, other speakers are likely director/reader — prefer talent's speech)`);
    }

    if (t.segments && t.segments.length > 0) {
      commentParts.push(`Transcript with timestamps:\n${timedSegmentLines(t.segments, { speakerLabels: true })}`);
    } else if (t.text) {
      commentParts.push(`Transcript: ${t.text}`);
    }

    const videoUrl = toAbsoluteS3Url(resolveVideoUrl(f.s3Url, f.s3Key));

    return {
      url: videoUrl || '',
      name: f.name,
      type: f.fileType || 'video',
      duration: String(f.duration || 0),
      comment: commentParts.join('\n'),
      liked: f.liked || false,
    };
  });
}

/** Variant 'refine' — minimal per-file payload for /api/script/refine. */
export function buildRefineAssets(files: EnhancerFile[]): any[] {
  return files.map(file => ({
    name: file.name,
    type: file.fileType === 'audio' ? 'audio' : 'video',
    duration: file.duration || 0,
    comment: (file.analysisData as any)?.transcript?.text ||
             (file.analysisData as any)?.transcript?.full_text || '',
  }));
}

/** Variant 'generate-plan' — per-file payload for /api/script/generate-plan. */
export function buildGeneratePlanAssets(files: EnhancerFile[]): any[] {
  return files.map(f => ({
    name: f.name,
    type: f.fileType || 'video',
    duration: f.duration,
    s3Url: f.s3Url,
    comment: (f.analysisData as any)?.transcript?.text || '',
    thumbnailUrl: f.thumbnailUrl,
  }));
}

/** Single parameterized entry point (dispatcher over the variant builders). */
export function buildAssetsMetadata(
  variant: AssetsVariant,
  ctx: {
    files: EnhancerFile[];
    analysisResults?: AnalysisResults;
    clips?: ProjectClip[];
    selectedFiles?: Set<string>;
    includeTranscripts?: boolean;
  },
): any[] | RenderAssetsResult {
  switch (variant) {
    case 'render':
      return buildRenderAssets({
        clips: ctx.clips || [],
        files: ctx.files,
        analysisResults: ctx.analysisResults || {},
        includeTranscripts: ctx.includeTranscripts !== false,
      });
    case 'script-analyze':
      return buildScriptAnalyzeAssets(ctx.files, ctx.analysisResults || {});
    case 'plan':
      return buildPlanAssets(ctx.files, ctx.selectedFiles || new Set(), ctx.analysisResults || {});
    case 'refine':
      return buildRefineAssets(ctx.files);
    case 'generate-plan':
      return buildGeneratePlanAssets(ctx.files);
  }
}
